import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import {
  Observable,
  switchMap,
  map,
  debounceTime,
  Subscription,
  combineLatest,
  BehaviorSubject,
  NEVER,
  take,
  withLatestFrom,
  of,
  catchError,
  distinctUntilChanged,
  throwError,
  iif,
  startWith,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { stopPropagation, unsubscribe } from '@fcom/utils';
import { Location, LocationGeoLocData, LocationMatchData, LocationSuggestions } from '@fcom/core-api';
import { AppState, GeolocationService, LatLng, LocationRouteCffService } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { akamaiGeolocation, isBrowserGeolocationLoading } from '@fcom/core/selectors';
import { finShare, retryWithBackoff } from '@fcom/rx';
import { isBlank } from '@fcom/utils/utils';
import { ButtonMode, ButtonSize, ButtonTheme, IconPosition, InputType, LoaderTheme } from '@fcom/ui-components';
import { ConsentService } from '@fcom/common/login';
import { ConsentTextId } from '@fcom/common';

import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { GtmLocationEventType, Locations } from '../../interfaces';

type LocationType = keyof Locations;

@Component({
  selector: 'fin-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSelectorComponent implements AfterViewInit, OnInit, OnDestroy {
  readonly LoaderTheme = LoaderTheme;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;
  readonly InputType = InputType;

  @Input()
  disabled = false;

  @Input()
  locations$: Observable<Locations>;

  @Input()
  suggestedLocations$: Observable<LocationSuggestions> = of(undefined);

  @Input()
  trendingDestinations$: Observable<Location[]> = of(undefined);

  @Input()
  showSeparator = true;

  @Input()
  showLocateMe = true;

  @Output()
  setLocations = new EventEmitter<Locations>();

  modalOpen = false;
  locationType$: BehaviorSubject<'origin' | 'destination'> = new BehaviorSubject('origin');
  GtmLocationEventType$: BehaviorSubject<'locations' | 'suggestions'> = new BehaviorSubject('locations');
  defaultOrigin$: Observable<Location>;
  origin$: Observable<Location>;
  destination$: Observable<Location>;
  enablePersonalization$: Observable<boolean>;
  defaultLocations$: Observable<Location[]>;
  locationResults$: BehaviorSubject<Location[]> = new BehaviorSubject(undefined);
  locationSearchForm: FormGroup<{ locationSearch: FormControl<string> }>;
  queryString$: Observable<string>;
  hasSearch$: Observable<boolean>;
  isGeoLocating$: Observable<boolean>;
  isLoading$ = new BehaviorSubject<boolean>(false);
  activeIndex$ = new BehaviorSubject<number>(0);

  locationsSwitched = new EventEmitter<void>();
  locationSelected = new EventEmitter<{ location: Location; locationType: LocationType }>();

  private subscriptions: Subscription = new Subscription();

  @ViewChild('locationListHeading') locationListHeading: ElementRef;
  @ViewChildren('locationItem', { read: ElementRef }) locationItems: QueryList<ElementRef>;

  constructor(
    private store$: Store<AppState>,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private geoLocationService: GeolocationService,
    private consentService: ConsentService,
    private bookingWidgetGtmService: BookingWidgetGtmService
  ) {}

  ngOnInit(): void {
    this.isGeoLocating$ = this.store$.pipe(isBrowserGeolocationLoading());

    this.defaultOrigin$ = this.store$.pipe(akamaiGeolocation()).pipe(
      switchMap((value: LatLng) =>
        this.locationRouteCffService.geolocMatchFor(value.lat, value.lng, this.languageService.localeValue)
      ),
      switchMap((location: LocationGeoLocData) =>
        this.locationRouteCffService.bestGuessFor(location.item.locationCode, this.languageService.localeValue)
      ),
      retryWithBackoff(2),
      catchError((err: unknown) => {
        return throwError(() => err);
      }),
      distinctUntilChanged(),
      finShare()
    );

    this.locationSearchForm = new FormGroup({
      locationSearch: new FormControl(''),
    });

    this.defaultLocations$ = combineLatest([
      this.suggestedLocations$,
      this.trendingDestinations$,
      this.locationType$,
    ]).pipe(
      map(([suggestedLocations, trendingLocations, locationType]: [LocationSuggestions, Location[], LocationType]) =>
        locationType === 'origin'
          ? suggestedLocations?.departures
          : trendingLocations
          ? suggestedLocations?.destinations
          : trendingLocations
      ),
      finShare()
    );

    this.enablePersonalization$ = this.consentService
      .getCookieConsentStatusById(ConsentTextId.COOKIE_PERSONALIZATION)
      .pipe(distinctUntilChanged());

    this.queryString$ = this.locationSearchForm
      .get('locationSearch')
      .valueChanges.pipe(debounceTime(500), startWith(''), finShare());

    this.subscriptions.add(
      this.queryString$.subscribe((queryString) => {
        this.GtmLocationEventType$.next(`${isBlank(queryString) ? 'locations' : 'suggestions'}`);
        this.sendGtmEvent(GtmLocationEventType.INPUT, queryString);
      })
    );

    this.subscriptions.add(
      this.queryString$
        .pipe(
          distinctUntilChanged(),
          switchMap((queryString: string) => {
            this.isLoading$.next(true);
            return iif(
              () => isBlank(queryString),
              of(undefined),
              this.locationRouteCffService
                .locationMatchesFor(queryString, this.languageService.localeValue)
                .pipe(map((data: LocationMatchData) => data.items))
            );
          })
        )
        .subscribe((locations: Location[]) => {
          this.locationResults$.next(locations);
          this.isLoading$.next(false);
        })
    );

    this.subscriptions.add(
      this.locationsSwitched
        .pipe(withLatestFrom(this.locations$))
        .subscribe(([, { origin, destination }]) =>
          this.setLocations.emit({ origin: destination, destination: origin })
        )
    );

    this.subscriptions.add(
      this.locationSelected
        .pipe(withLatestFrom(this.locations$))
        .subscribe(([{ location, locationType }, currentLocations]) => {
          const oppositeLocationType = locationType === 'origin' ? 'destination' : 'origin';

          const newLocations =
            location.locationCode === currentLocations[oppositeLocationType]?.locationCode
              ? {
                  [locationType]: location,
                  [oppositeLocationType]: undefined,
                }
              : {
                  ...currentLocations,
                  [locationType]: location,
                };

          this.sendGtmEvent(GtmLocationEventType.SELECTION, location.locationCode);
          this.setLocations.emit(newLocations);
          this.closeModal();
        })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.activeIndex$.subscribe((i) => {
        this.locationItems.get(i)?.nativeElement?.focus();
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  openModal(type: keyof Locations): void {
    this.modalOpen = true;
    this.locationType$.next(type);
    this.locationSearchForm.reset({ locationSearch: '' }, { emitEvent: false });
  }

  closeModal(): void {
    this.modalOpen = false;
    this.resetSearch();
    this.sendGtmEvent(GtmLocationEventType.CLOSE, this.locationSearchForm.get('locationSearch').value);
  }

  resetSearch(): void {
    this.locationSearchForm.controls.locationSearch.setValue('');
    this.locationResults$.next(undefined);
    this.isLoading$.next(false);
    this.activeIndex$.next(0);
  }

  moveFocusToResults(e: Event): void {
    stopPropagation(e);
    this.locationListHeading?.nativeElement?.focus();
  }

  getUserLocation(): void {
    this.subscriptions.add(
      this.geoLocationService
        .getGeolocationFromNavigator(NEVER)
        .pipe(take(1))
        .subscribe((location: Location) => {
          this.closeModal();
          this.setLocations.emit({ origin: location });
        })
    );
  }

  handleKeyDown(e: KeyboardEvent, locations: Location[]): void {
    const currentActiveIndex = this.activeIndex$.getValue();
    const maxIndex = locations ? locations.length - 1 : 0;

    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        stopPropagation(e);
        this.activeIndex$.next(currentActiveIndex + 1 > maxIndex ? 0 : currentActiveIndex + 1);
        break;
      case 'Up':
      case 'ArrowUp':
        stopPropagation(e);
        this.activeIndex$.next(currentActiveIndex - 1 < 0 ? maxIndex : currentActiveIndex - 1);
        break;
      default:
        break;
    }
  }

  private sendGtmEvent(eventType: GtmLocationEventType, state: string): void {
    this.bookingWidgetGtmService.trackElementEvent(
      `${this.locationType$.getValue()}-${this.GtmLocationEventType$.getValue()}-modal-${eventType}`,
      state
    );
  }
}
